import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Form, Col, Tooltip, Result, Empty } from '@bedrock/components'
import Error from "@bedrock/components/es/Empty/images/small/Error";
import { ColProps } from '@bedrock/components/lib/Col'
import { HelpCircle } from '@bedrock/icons-react'
import { ErrorBoundary } from "react-error-boundary";
import SFormElement, { SFormComponentClass } from "../SFormElement";
import cx from 'classnames'
import './index.less'
import {formItemKeyToList, DefaultRequiredRuleMap} from "#components/SForm/SFormItem/utils";
import FormItemValueTransform from "#components/SForm/SFormItem/FormItemValueTransform";
import { ScoutCaptureError } from '#utils/scout';
import _ from 'lodash';
import { TooltipProps } from '@bedrock/components/es/Tooltip';
import { useMemoizedFn } from 'ahooks';


export const getLabelTip = (label, labelTip) => {
  let toolTipProps = {};
  if (_.isObject(labelTip) && !React.isValidElement(labelTip)) {
    toolTipProps = labelTip;
  }
  return (
      <>
        <span style={{ marginRight: '4px' }}>{label}</span>
        <span className='label-icon'>
          <Tooltip title={labelTip} placement='top' {...toolTipProps}>
            <HelpCircle />
          </Tooltip>
        </span>
      </>
  )
}

// 函数生成T或者T本身
type AbleFunc<T, params = any> = T | ((params: params) => T)

type AbleFuncObj<T> = {
  [P in keyof T]: AbleFunc<T[P], any>
}

export type FormItemKeyType = (string | number)[] | string | number;

export type SFormItemPropsData<T extends SFormComponentClass = SFormComponentClass> = {
  formItemKeyMap?: Record<string, FormItemKeyType>;
  formItem?: boolean;
  formItemKey: FormItemKeyType;
  renderData?: any;
  label?: string | number;
  labelTip?: string | Partial<TooltipProps> | React.ReactElement;
  labelText?: string;
  required?: boolean;
  componentClass?: T;
  className?: string;
  view?: boolean;
  show?: boolean;
  col?: number | ColProps; // 布局
  offsetAfter?: number; // 偏移
  elementProps?: any; // 使用 any 避免类型约束问题
  valueFormat?: {
    /**
     * 外部value转为内部fieldValue
     * @param value 外部传入的value
     * @param fieldProps 字段属性
     * @returns 内部控件可用的fieldValue
     */
    valueToField?: (value: any, fieldProps: any) => any
    /**
     * 内部fieldValue转为外部value
     * @param fieldValue 控件内部的值
     * @param fieldProps 字段属性
     * @returns 外部表单需要的value
     */
    fieldToValue?: (fieldValue: any, fieldProps: any) => any
  },
  hideError?: boolean; // 是否隐藏错误提示
  render?: (renderData: any, itemProps: SFormItemPropsData) => React.ReactElement;
  labelWrapper?: (props: { label: any }, renderData: any) => React.ReactElement;
  watchChildFormValidate?: boolean | 'string'; // 是否监听子元素Form的校验，子元素的ref如果有validateFields方法，会被添加进校验队列
} & Omit<React.ComponentProps<typeof Form.Item>, 'label' | 'required' | 'name' | 'className'>;

export type SFormItemProps<T extends FormItemKeyType = FormItemKeyType, U extends SFormComponentClass = SFormComponentClass> = {
  formItemKey: T;
} & AbleFuncObj<Omit<SFormItemPropsData<U>, 'formItemKey'>>

// 函数参数，不再转化
const FUNCTION_CONFIG = ['render', 'labelWrapper']

// AbleFuncObj 转化为 SFormItemPropsData
export const configFunctionToValue = (props: SFormItemProps, renderData: any):SFormItemPropsData => {
  // 如果是函数，就执行
  const { ...rets } = props;
  const renderProps = Object.keys(rets).reduce((prev, key) => {
    const value = props[key];
    if (FUNCTION_CONFIG.includes(key)) {
      prev[key] = value;
      return prev;
    }
    prev[key] = typeof value === 'function' ? value(renderData) : value;
    return prev;
  }, {});
  return renderProps as SFormItemPropsData;
}

export default function SFormItem<
    T extends FormItemKeyType = FormItemKeyType,
    U extends SFormComponentClass = SFormComponentClass
>(props: SFormItemProps<T, U>): React.ReactElement {

  const renderData = props.renderData || {};

  const renderProps = useMemo(() => {
    return configFunctionToValue(props, renderData);
  }, [props])


  const {
    formItemKeyMap = null,
    formItem = true,
    label,
    labelTip,
    labelText,
    labelWrapper,
    required,
    formItemKey,
    componentClass,
    className,
    view,
    show = true,
    elementProps = {},
    rules,
    col,
    offsetAfter,
    dependencies,
    watchChildFormValidate,
    hideError = false,
    valueFormat,
    ...itemProps
  } = renderProps;

  const elementRef = useRef(null);

  // 创建包装器组件来处理 valueFormat
  const WrappedSFormElement = useMemo(() => {
    if (!valueFormat) {
      return SFormElement;
    }

    const Wrapper: React.FC<any> = (fieldProps) => {
      const { value, onChange, ...restFieldProps } = fieldProps;
      const restFieldPropsRef = useRef(restFieldProps);
      restFieldPropsRef.current = restFieldProps;

      const [internalValue, setInternalValue] = useState<any>(() => {
        // 初始化时，如果有外部value，转换为内部值
        if (value !== undefined && valueFormat.valueToField) {
          return valueFormat.valueToField(value, restFieldPropsRef.current);
        }
        return value;
      });

      // 当外部value变化时，同步到内部
      useEffect(() => {
        if (value !== undefined && valueFormat.valueToField) {
          const newInternalValue = valueFormat.valueToField(value, restFieldPropsRef.current);
          setInternalValue(newInternalValue);
        } else {
          setInternalValue(value);
        }
      }, [value, valueFormat.valueToField]);

      const handleInternalChange = useMemoizedFn((newInternalValue: any, ...args: any[]) => {
        // 转换为外部值并调用外部onChange
        if (onChange && valueFormat.fieldToValue) {
          const externalValue = valueFormat.fieldToValue(newInternalValue, restFieldPropsRef.current);
          onChange(externalValue, ...args);
        } else if (onChange) {
          onChange(newInternalValue, ...args);
        }
      });

      return <SFormElement {...restFieldProps} value={internalValue} onChange={handleInternalChange} />;
    };

    Wrapper.displayName = "ValueFormatWrapper";
    return Wrapper;
  }, [valueFormat]);

  const ablerules:any = useMemo(() => {
    const ruleList = [...(rules || [])];
    if (view) return [];
    if (required) {
      if (DefaultRequiredRuleMap[componentClass]) {
        ruleList?.unshift?.({ label, message: `此项必填`, ...DefaultRequiredRuleMap[componentClass] })
      } else {
        ruleList?.unshift?.({ required: true, message: `此项必填` })
      }
    }

    if (watchChildFormValidate) {
      let validateFnName = 'validateFields'
      if (typeof watchChildFormValidate === 'string') {
        validateFnName = watchChildFormValidate
      }
      ruleList.push({
        validator: (rule, value) => {
          if (!elementRef.current?.[validateFnName]) {
            return Promise.resolve()
          }
          // 兜底优化，如果方法返回的不是promise，则promise包裹
          return Promise.resolve(elementRef.current?.[validateFnName]?.())
        }
      });
    }

    return ruleList;
  }, [required, rules, view, watchChildFormValidate])

  const showRequired = useMemo(() => {
    if (view) return false;
    if (required) {
      return true;
    }
    return ablerules.some((rule) => rule.required);
  }, [required, view, ablerules])

  const colProps = useMemo(() => {
    if (typeof col === 'number') {
      return { span: col }
    }
    return col
  }, [col])

  if (!show) return null;

  let item: any = props.render ? (props?.render?.(props.renderData, renderProps) || null) :
      <WrappedSFormElement
          componentClass={componentClass}
          view={view}
          {...elementProps}
      />;

  // 写入item写入ref
  if (watchChildFormValidate && React.isValidElement(item) && !(item as any)?.ref?.current) {
    item = React.cloneElement(item as React.ReactElement, { ref: elementRef });
  }

  // 如果是formItem，就包裹一层Form.Item
  const formItemConfig = {
    label: labelTip ? getLabelTip(label, labelTip) : label,
    required: showRequired,
    name: formItemKeyToList(formItemKey),
    rules: ablerules,
    className: cx('s-form-item', {
      'form_item_hidden_error': hideError,
    }, className),
    dependencies: dependencies?.map((n) => formItemKeyToList(n)),
    ...itemProps
  }
  // console.log('formItemConfig依赖', formItemConfig)
  if (labelWrapper) {
    formItemConfig.label = labelWrapper({ label: formItemConfig.label }, renderData)
  }

  if (formItemKeyMap){
    item = <FormItemValueTransform
        keyMap={formItemKeyMap}
        {...formItemConfig}
    >
      {item}
    </FormItemValueTransform>
  } else if (formItem) {
    item = <Form.Item
        {...formItemConfig}
    >
      {item}
    </Form.Item>
  }

  item = <ErrorBoundary
      FallbackComponent={({ error }) => {
        ScoutCaptureError(error, 'SFormItemError');
        return <Empty
            style={{
              width: '100%',
              minWidth: '400px'
            }}
            imageNode={<Error />}
            text="渲染错误"
            description={'SformItem渲染错误'}
        />
      }}
  >
    {item}
  </ErrorBoundary>

  // 如果有col，就包裹一层Col
  if (!!col) {
    item = <>
      <Col {...colProps}>{item}</Col>
      {offsetAfter && <Col span={offsetAfter} />}
    </>
  }

  return item as React.ReactElement;
}
